import axios from 'axios';
import { Col, Icon, Row, Spinner2, Text } from 'components';
import { isMobile, streaming as streamingHandler } from 'core';
import { getDuration } from 'lib';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import io from 'socket.io-client';
import styled from 'styled-components';

import { Chat, ChatButton, LocalVideo, Video } from './components';

const API_URL = process.env.REACT_APP_API_URL;
const WS_URL = process.env.REACT_APP_WS_URL;

const ConferenceContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: ${(props) => (props.isMobile ? 'column-reverse' : 'row')};
  width: 100%;
`;

const Buttons = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  bottom: 0;
  right: 0;
  padding-bottom: 25px;
  box-shadow: 0 -17px 46px 45px rgb(0 0 0 / 20%);
  background-color: rgba(0, 0, 0, 0.2);
`;

const IconContainer = styled.div`
  cursor: pointer;
  padding: ${(props) => props.pad || '20px'};
  border-radius: 100%;
  background-color: ${(props) => props.bg};
  ${(props) => props.marg && `margin: ${props.marg};`}
`;

let streamTimer;
const Conference = () => {
  const [loading, setLoading] = useState(true),
    [token, setToken] = useState(null),
    [streams, setStreams] = useState([]),
    [finalText, setFinalText] = useState(false),
    [muted, setMuted] = useState(false),
    [videoActive, setVideoActive] = useState(true),
    [chatVisible, setChatVisible] = useState(false),
    [unreadCount, setUnreadCount] = useState(0),
    [seconds, setSeconds] = useState(0),
    [contentHt, setContentHt] = useState(window.innerHeight + 'px'),
    [messages, setMessages] = useState([]),
    { streaming } = useSelector((state) => state),
    { roomId } = useParams(),
    { hash } = useLocation(),
    userName = hash.slice(1),
    // in order to access latest state from event listeners
    messagesRef = useRef(messages),
    chatVisibleRef = useRef(chatVisible),
    unreadCountRef = useRef(unreadCount),
    dispatch = useDispatch();

  useEffect(() => {
    if (token) return;

    getToken();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setSeconds(seconds + 1);
    }, 1000);
  }, [seconds]);

  // SENT ICE CANDIDATES SOMETIMES LEAKS MEMORY
  // THIS IS A GUARD FOR MEMORY LEAKS
  useEffect(() => {
    // CLEAR INTERVAL ON ICE CAND.
    clearTimeout(streamTimer);

    // DO THIS EACH SECOND UNTIL CONNECTION IS COMPLETE
    streamTimer = setTimeout(() => {
      // list of ice states: new, checking, connected, completed, failed, disconnected, closed
      const mapped = Object.keys(streaming.clients)
        .map((key) => {
          const { clients } = streaming;

          const isNotConnected = ['failed', 'disconnected', 'closed'].includes(
            clients[key].connectionState
          );

          if (isNotConnected) return null;

          return clients[key];
        })
        .filter((client) => client);

      setStreams(mapped);
    }, 1000);
  }, [streaming]);

  function handleChatVisible() {
    chatVisibleRef.current = !chatVisible;
    setChatVisible(!chatVisible);
  }

  // INITIATES CURRENT CALL CONNECTION
  async function getToken() {
    dispatch({ type: 'INIT_STATE' });

    // const token = await Api.post('/conferences/getToken', { roomId, userName })
    const token = await axios
      .post(`${API_URL}/conferences/getToken`, { roomId, userName })
      .then((r) => r.data.token)
      .catch((e) => {
        setFinalText(e.response.data);
        setLoading(false);
      });

    if (!token) return;

    setToken(token);
    setLoading(false);

    // TODO: HANDLE MIC & SPEAKER PERMISSIONS
    await streamingHandler.localStream();
    handleWs(token);
  }

  // INITIATE WS CONNECTION
  // WEBRTC CONNECTION COMMUNICATIONS AND CHAT MESSAGES ARE LISTENED HERE
  async function handleWs(jwt) {
    const socket = io(WS_URL, { query: { token: jwt } });

    socket.on('error', (data) => {
      console.log('socket error', data);
    });

    socket.on('reconnect_error', (data) => {
      console.log('socket reconnect_error', data);
    });

    global.socket = socket;

    socket.on('connect', () => {});

    socket.on('newMessage', (data) => {
      handleMessages(data);
    });

    socket.on('alreadyConnected', () => {
      setFinalText('This link is already connected from another window or device.');
      global.socket.disconnect();
      streamingHandler.close();
    });

    socket.on('newOffer', (data) => {
      if (data.offerTo !== userName || finalText) return;
      streamingHandler.onOffer({ ...data, currentUserName: userName });
    });

    socket.on('newAnswer', (data) => {
      if (data.answerTo !== userName) return;
      streamingHandler.onAnswer({ ...data, currentUserName: userName });
    });

    socket.on('iceCandidate', (data) => {
      if (data.to !== userName) return;
      streamingHandler.addIceCandidate(data);
    });

    // A NEW USER JOINED THE ROOM, SIGNAL THAT USER AND ASK FOR OFFER
    // THIS ACTION HAPPENS PER PEER
    socket.on('userConnected', (data) => {
      if (data.userName === userName) return;
      socket.send('confirmConnection', { to: data.userName, from: userName });
    });

    // A NEW PEER'S CONNECTION TO THE ROOM CONFIRMED BY ANOTHER SINGLE PEER
    // ALREADY CONNECTED TO THE ROOM, SEND OFFER TO CONFIRMING PEER
    // THIS ACTION HAPPENS PER PEER
    socket.on('confirmConnection', (data) => {
      if (data.to !== userName) return;
      streamingHandler.sendOffer({ offerTo: data.from, currentUserName: userName });
    });

    socket.on('hangUp', (data) => {
      streamingHandler.hangUp(data);
    });
  }

  function sendWs(event, payload) {
    global.socket.send(event, payload);
  }

  function handleMessages(newMsg) {
    // to access current state from event listeners
    const newMessagesState = [...messagesRef.current, newMsg];
    const newUnreadCount = unreadCountRef.current + 1;
    const isChatVisible = chatVisibleRef.current;

    messagesRef.current = newMessagesState;
    setMessages(newMessagesState);

    if (isChatVisible || newMsg.sender === userName) return;
    handleUnreadCount(newUnreadCount);
  }

  function handleUnreadCount(newCount) {
    unreadCountRef.current = newCount;
    setUnreadCount(newCount);
  }

  function hangUp() {
    streamingHandler.close();
    sendWs('hangUp', { userName });
    global.socket.disconnect();
    setFinalText('You left the conference.');
  }

  async function handleMute() {
    if (muted) {
      setMuted(false);
      streamingHandler.toggleMute();

      return;
    }

    streamingHandler.toggleMute();
    setMuted(true);
  }

  async function handleVideo() {
    if (videoActive) {
      setVideoActive(false);
      streamingHandler.toggleVideo();

      return;
    }

    streamingHandler.toggleVideo();
    setVideoActive(true);
  }

  return (
    <Row ht="100%" id="conference">
      <Col ht="100%" bg="black" centerAll={!token || finalText} pos="relative">
        {!!loading && !finalText && <Spinner2 spinnerSize="70px" />}

        {!loading && !finalText && (
          <ConferenceContainer id="videos" isMobile={window.innerWidth < 1024}>
            {!!streaming.local && (
              <LocalVideo
                stream={streaming.local}
                isFullScreen={!streams.length}
                isActive={videoActive}
              />
            )}

            {streams.map((client, i) => (
              <Video key={i} idx={i} client={client} />
            ))}
          </ConferenceContainer>
        )}

        {token && !finalText && (isMobile ? !chatVisible : true) && (
          <React.Fragment>
            <Buttons>
              <Text col="#fff">{getDuration(seconds)}</Text>

              <Row center marg="10px 0 0">
                {/*<IconContainer pad='15px' bg='grey' marg='0 20px 0 0' onClick={testActions}>
                  <Icon type={videoActive ? 'videocam' : 'videocam_off'} dimensions={25}/>
                </IconContainer>*/}

                <IconContainer pad="15px" bg="grey" marg="0 20px 0 0" onClick={handleVideo}>
                  <Icon type={videoActive ? 'videocam' : 'videocam_off'} dimensions={25} />
                </IconContainer>

                <IconContainer bg="red" marg="0 20px 0 0" col="white" onClick={hangUp}>
                  <Icon type="call_end" col="white" dimensions={30} />
                </IconContainer>

                <IconContainer pad="15px" bg="grey" onClick={handleMute}>
                  <Icon type={muted ? 'mic_off' : 'mic'} dimensions={25} />
                </IconContainer>

                <ChatButton
                  chatVisible={chatVisible}
                  handleChatVisible={handleChatVisible}
                  unreadCount={unreadCount}
                />
              </Row>
            </Buttons>
          </React.Fragment>
        )}

        {finalText && (
          <Text col="#fff" size="30px">
            {finalText}
          </Text>
        )}
      </Col>

      {!finalText && (
        <Chat
          chatVisible={chatVisible}
          currentUser={userName}
          sendWs={sendWs}
          messages={messages}
          handleUnreadCount={handleUnreadCount}
          handleChatVisible={handleChatVisible}
          contentHt={contentHt}
          setContentHt={setContentHt}
        />
      )}
    </Row>
  );
};

export default Conference;
